import React from "react";
import { Link } from "react-router-dom";
import { Check, ArrowRight } from "lucide-react";
import {
  Accordion, AccordionContent, AccordionItem, AccordionTrigger
} from "../components/ui/accordion";

const SAP_LOGO = "https://form-elec.fr/wp-content/uploads/2024/01/logosapgrandombre-1024x754.png";
const CREDIT_IMG = "https://form-elec.fr/wp-content/uploads/2024/01/credit-impot-seance-sport-electrostimulation-istres.png";

const FAQ = [
  { q: "Qui gère ce service ?", a: "Le service Avance immédiate est proposé par l'URSSAF et la Direction générale des Finances publiques (DGFiP)." },
  { q: "Qu'est-ce que l'avance immédiate de crédit d'impôt ?", a: "Le service Avance immédiate vous permet de déduire le montant de votre crédit d'impôt du montant dû à votre organisme prestataire (Form'Elec)." },
  { q: "Quelles sont les conditions pour bénéficier du service ?", a: "Votre organisme de services à la personne doit en avoir fait la demande auprès de l'URSSAF (Form'Elec est agréé). Vous devez disposer d'un compte bancaire domicilié en France, avoir un numéro fiscal associé à votre état civil et avoir déjà effectué au moins une déclaration de revenus." },
  { q: "Quel sera l'impact sur la facturation ?", a: "L'inscription au service vous donne accès à un compte sur particulier.urssaf.fr. Form'Elec vous transmet des demandes de paiement, vous validez sous 48h, puis l'URSSAF prélève uniquement le reste à charge (50%) sur votre compte bancaire dans les 2 jours ouvrés." },
  { q: "Qu'est-ce qu'une demande de paiement ?", a: "Une demande de paiement indique le montant total des prestations réalisées ainsi que votre reste à payer après déduction du crédit d'impôt. Reste à payer = montant de la prestation − crédit d'impôt." },
  { q: "Comment accéder à mes demandes de paiement ?", a: "Connectez-vous sur particulier.urssaf.fr, rubrique « Paiement » > « Tous mes paiements »." },
  { q: "Vais-je toujours recevoir une attestation fiscale ?", a: "Oui, Form'Elec vous remet chaque année une attestation fiscale récapitulant l'ensemble des prestations réalisées." },
  { q: "Quel est le plafond de crédit d'impôt ?", a: "Le plafond annuel d'avance immédiate est de 2 500 € (donc 5 000 € de dépenses). Le plafond global du crédit d'impôt services à la personne est de 6 000 € par an et par foyer fiscal." },
];

export default function CreditImpot() {
  return (
    <div className="bg-white">
      {/* Hero */}
      <section className="bg-fegray-light pt-16 pb-16 px-6 lg:px-10" data-testid="credit-hero">
        <div className="max-w-7xl mx-auto text-center">
          <p className="text-fegreen font-medium uppercase tracking-[0.25em] text-xs mb-4">
            — Avance Crédit d&apos;Impôts
          </p>
          <h1 className="text-4xl lg:text-6xl font-bold text-fegray-dark">
            Faites l&apos;économie de <span className="text-fegreen">50%</span>
            <br />sur vos séances de sport !
          </h1>
        </div>
      </section>

      {/* Présentation */}
      <section className="py-16 lg:py-20 px-6 lg:px-10">
        <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-7">
            <h2 className="text-2xl lg:text-3xl font-bold text-fegray-dark mb-6">
              Avance crédit d&apos;impôts sur vos séances d&apos;électrostimulation
            </h2>
            <p className="text-fegray leading-relaxed mb-4">
              L&apos;<span className="font-bold">Avance de crédit d&apos;impôt</span>,
              vous connaissez ? L&apos;avantage fiscal anciennement crédit d&apos;impôt
              sur le revenu égal à 50% des dépenses engagées peut désormais être
              déduit immédiatement de votre facture pour les prestations de
              services comme nos <Link to="/tarifs" className="text-fegreen font-semibold underline">séances d&apos;électrostimulation</Link>.
              Vous n&apos;avez plus d&apos;avance de frais à faire !
            </p>
            <p className="text-fegray leading-relaxed mb-6">
              Dans la limite de <span className="font-bold">6 000€ par an et par foyer fiscal</span>,
              vous pouvez profiter de cet avantage pour mettre en place votre
              programme de coaching en toute sérénité.
            </p>
            <a
              href="https://www.urssaf.fr/portail/home/services-a-la-personne/client-dorganisme-de-service-a-l/client-de-prestataire/le-service-avance-immediate-ques.html"
              target="_blank"
              rel="noreferrer"
              className="btn-outline-green"
            >
              Plus d&apos;infos sur urssaf.fr <ArrowRight className="w-4 h-4" />
            </a>
          </div>
          <div className="lg:col-span-5 flex justify-center">
            <div className="fe-card p-6 lg:p-8">
              <img src={CREDIT_IMG} alt="Crédit d'impôt sur séance EMS" className="w-full max-w-sm h-auto rounded-xl" />
              <img src={SAP_LOGO} alt="Services à la personne" className="w-48 mx-auto mt-6" />
            </div>
          </div>
        </div>
      </section>

      {/* Exemple */}
      <section className="py-16 px-6 lg:px-10 bg-fegray-light">
        <div className="max-w-5xl mx-auto fe-card p-8 lg:p-12">
          <h2 className="text-2xl lg:text-3xl font-bold text-fegray-dark text-center mb-3">
            Exemple d&apos;avance crédit d&apos;impôt
          </h2>
          <p className="text-fegreen text-center mb-10">
            sur séance d&apos;électrostimulation
          </p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 text-center">
            <div>
              <p className="text-sm text-fegray uppercase tracking-wider mb-2">Tarif initial</p>
              <p className="text-5xl font-extrabold text-fegray-dark">100€</p>
              <p className="text-xs text-fegray mt-2">de services à la personne</p>
            </div>
            <div className="flex items-center justify-center">
              <ArrowRight className="w-12 h-12 text-fegreen" />
            </div>
            <div>
              <p className="text-sm text-fegreen uppercase tracking-wider mb-2 font-bold">Reste à charge</p>
              <p className="text-5xl font-extrabold text-fegreen">50€</p>
              <p className="text-xs text-fegray mt-2">après déduction 50% URSSAF</p>
            </div>
          </div>
          <p className="text-sm text-fegray text-center mt-8 italic">
            L&apos;avance immédiate de crédit d&apos;impôts est un service gratuit qui
            permet la déduction immédiate de votre crédit d&apos;impôt de 50% lors du
            paiement de votre facture Form&apos;Elec.
          </p>
        </div>
      </section>

      {/* 4 étapes */}
      <section className="py-16 lg:py-20 px-6 lg:px-10">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-2xl lg:text-3xl font-bold text-fegray-dark text-center mb-2">
            Comment ça marche ?
          </h2>
          <p className="text-fegreen text-center mb-14">4 étapes simples</p>

          <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
            {[
              { num: "01", title: "Inscription URSSAF", text: "Form'Elec vous inscrit au service Avance immédiate sur particulier.urssaf.fr." },
              { num: "02", title: "Séances réalisées", text: "Vous profitez de vos séances d'EMS à domicile avec Rosalie." },
              { num: "03", title: "Demande de paiement", text: "Form'Elec émet une demande mensuelle. Vous validez sous 48h." },
              { num: "04", title: "Prélèvement -50%", text: "L'URSSAF prélève uniquement le reste à charge (50%) sur votre compte." },
            ].map((s) => (
              <div key={s.num} className="fe-card p-7">
                <div className="text-4xl font-extrabold text-fegreen mb-3">{s.num}</div>
                <h3 className="text-lg font-bold text-fegray-dark mb-3">{s.title}</h3>
                <p className="text-sm text-fegray leading-relaxed">{s.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Vidéo URSSAF */}
      <section className="py-16 px-6 lg:px-10">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-2xl lg:text-3xl font-bold text-fegray-dark text-center mb-3">
            Votre crédit d&apos;impôt évolue
          </h2>
          <p className="text-fegreen text-center mb-8">avec l&apos;avance immédiate (vidéo URSSAF officielle)</p>
          <div className="rounded-2xl overflow-hidden shadow-lg aspect-video">
            <iframe
              src="https://www.youtube.com/embed/JDFb4OTL39g"
              title="URSSAF — Avance immédiate crédit d'impôt"
              allow="encrypted-media; picture-in-picture"
              allowFullScreen
              className="w-full h-full"
            />
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-16 lg:py-20 px-6 lg:px-10 bg-fegray-light" data-testid="credit-faq">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-2xl lg:text-3xl font-bold text-fegray-dark text-center mb-3">
            Questions fréquentes
          </h2>
          <p className="text-fegreen text-center mb-10">
            sur l&apos;avance crédit d&apos;impôt
          </p>
          <Accordion type="single" collapsible className="w-full space-y-3">
            {FAQ.map((item, i) => (
              <AccordionItem
                key={i}
                value={`item-${i}`}
                className="fe-card border-0 px-6"
                data-testid={`faq-item-${i}`}
              >
                <AccordionTrigger className="text-left text-base font-semibold text-fegray-dark hover:text-fegreen hover:no-underline py-5">
                  {item.q}
                </AccordionTrigger>
                <AccordionContent className="text-fegray leading-relaxed pb-5">
                  {item.a}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 px-6 lg:px-10 bg-fegreen text-white text-center">
        <h2 className="text-3xl lg:text-4xl font-bold mb-4">
          Prêt à profiter du crédit d&apos;impôt ?
        </h2>
        <p className="text-white/90 mb-8 max-w-xl mx-auto">
          Réservez votre première séance et bénéficiez immédiatement de la
          déduction 50%.
        </p>
        <Link to="/reservation" className="btn-dark bg-white !text-fegreen hover:!bg-fegray-dark hover:!text-white">
          Réserver une séance <ArrowRight className="w-4 h-4" />
        </Link>
      </section>
    </div>
  );
}
